import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Header from "../organisms/Header";
import Footer from "../organisms/Footer";
import SermonView from "../organisms/SermonView";
import { analyticsService } from "../../services/AnalyticsService";
import {
  useAuth,
  useDisableScrollSnap,
  useSermon,
  useModal,
} from "../../util/hook";

const SermonDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getSermon, deleteSermon, incrementLocalViewCount } = useSermon();
  const { isLoggedIn } = useAuth();
  const { showAlert, showConfirm } = useModal();

  const sermon = getSermon(Number(id));

  useEffect(() => {
    if (id) {
      const trackView = async () => {
        const incremented = await analyticsService.incrementViewCount(
          "sermon",
          Number(id),
        );
        if (incremented) {
          incrementLocalViewCount(Number(id));
        }
      };
      trackView();
    }
  }, [id, incrementLocalViewCount]);

  const handleDelete = async () => {
    const confirmed = await showConfirm("정말로 삭제하시겠습니까?");
    if (confirmed) {
      try {
        await deleteSermon(Number(id));
        await showAlert("삭제되었습니다.");
        navigate("/");
      } catch (error) {
        console.error("Error deleting sermon:", error);
        await showAlert("삭제 중 오류가 발생했습니다. 다시 시도해주세요.");
      }
    }
  };

  const handleEdit = () => {
    navigate(`/sermon/edit/${id}`, {
      replace: true,
      state: { backTo: location.state?.backTo },
    });
  };

  useDisableScrollSnap();

  if (!sermon) {
    return (
      <PageWrapper>
        <Header />
        <Container>
          <NotFound>설교를 찾을 수 없습니다.</NotFound>
        </Container>
        <Footer />
      </PageWrapper>
    );
  }

  const handleGoBack = () => {
    if (location.state?.backTo) {
      navigate(location.state.backTo, { replace: true });
      return;
    }

    navigate(-1);
  };

  return (
    <>
      <Header />
      <Container>
        <BackButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> 돌아가기
        </BackButton>

        {isLoggedIn && (
          <AdminButtons>
            <AdminButton onClick={handleEdit} className="edit">
              수정
            </AdminButton>
            <AdminButton onClick={handleDelete} className="delete">
              삭제
            </AdminButton>
          </AdminButtons>
        )}

        <SermonView sermon={sermon} />
      </Container>
      <Footer />
    </>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  flex: 1;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const BackButton = styled.button`
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 12px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  margin-bottom: 30px;
  transition: 0.3s;

  &:hover {
    background: #e0e0e0;
  }

  svg {
    margin-right: 8px;
  }
`;

const AdminButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: -60px; /* Pull up to align with back button row or similar */
  margin-bottom: 30px;

  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const AdminButton = styled.button`
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  border: none;
  font-weight: 700;

  &.edit {
    background: #4caf50;
    color: white;
  }

  &.delete {
    background: #f44336;
    color: white;
  }
`;

const NotFound = styled.div`
  text-align: center;
  padding: 100px 20px;
  font-size: 18px;
  color: #666;
`;

export default SermonDetail;
